import * as React from 'react';

import { Box, Stack } from '@mui/material';

import { FactureAchat, ReminderPublisherApiObject } from '@europrocurement/l2d-domain';
import EligibilityStatusChip from './EligibilityStatusChip';
import InvoiceEligibilityDetails from './InvoiceEligibilityDetails';

interface SingleInvoiceEligibilityInfoProps {
    factureAchat: FactureAchat;
    reminderPublisher?: ReminderPublisherApiObject;
}

/**
 * Composant complet d'information d'éligibilité pour une facture individuelle
 *
 * Composant composite utilisé dans les cellules du tableau des factures associées.
 * Combine le chip d'état d'éligibilité et l'accordéon de détails en mode compact.
 * Se charge de retrouver les données d'éligibilité à partir de l'ID de la facture.
 *
 * @param factureAchat - La facture pour laquelle afficher les infos d'éligibilité
 * @param reminderPublisher - L'objet rappel contenant les relations et données d'éligibilité
 */
const SingleInvoiceEligibilityInfo: React.FunctionComponent<SingleInvoiceEligibilityInfoProps> =
    function ({ factureAchat, reminderPublisher }) {
        const reminderPublisherFacture = reminderPublisher?.reminderPublisherFactures?.find(
            (rpf) => rpf?.factureAchat?.id === factureAchat.id,
        );

        if (
            !reminderPublisherFacture?.eligibleRejectionCodesInfo ||
            typeof reminderPublisherFacture.eligibleRejectionCodesInfo !== 'object'
        ) {
            return null;
        }

        const eligibilityInfo = reminderPublisherFacture.eligibleRejectionCodesInfo;

        const hasEligibleCodes = Boolean(eligibilityInfo.hasEligibleCodes);
        const eligibleCount = Number(eligibilityInfo.eligibleCodes) || 0;
        const totalCount = Number(eligibilityInfo.totalCodes) || 0;
        const eligibleCodes = Array.isArray(eligibilityInfo.eligibleCodesList)
            ? eligibilityInfo.eligibleCodesList
            : [];
        const ineligibleCodes = Array.isArray(eligibilityInfo.ineligibleCodesList)
            ? eligibilityInfo.ineligibleCodesList
            : [];

        return (
            <Box sx={{ maxWidth: 300 }}>
                <Stack
                    direction="column"
                    spacing={0.5}
                    alignItems="flex-start"
                    sx={{ mb: 0.5 }}
                >
                    <EligibilityStatusChip
                        hasEligibleCodes={hasEligibleCodes}
                        eligibleCount={eligibleCount}
                        totalCount={totalCount}
                        displayMode="compact"
                    />
                </Stack>

                <InvoiceEligibilityDetails
                    eligibleCodes={eligibleCodes}
                    ineligibleCodes={ineligibleCodes}
                    compact
                />
            </Box>
        );
    };

export default SingleInvoiceEligibilityInfo;
